// API Configuration

/** Loopback, or a private LAN address — the machine this is being developed on. */
const isLocalHost = (hostname: string): boolean =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '[::1]' ||
  hostname === '::1' ||
  hostname.endsWith('.local') ||
  /^10\./.test(hostname) ||
  /^192\.168\./.test(hostname) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);

/**
 * Point a configured API URL at the host the page was opened on.
 *
 * `http://localhost:6019` means "this device" — so on a phone or another machine
 * opening the dev server over the LAN it means *that* device, and every call fails.
 * The port and scheme are what matter; the host should follow the page.
 */
const onPageHost = (apiUrl: string, pageHostname: string): string => {
  try {
    const url = new URL(apiUrl);
    if (!isLocalHost(url.hostname) || url.hostname === pageHostname) return apiUrl;
    url.hostname = pageHostname;
    return url.toString().replace(/\/$/, '');
  } catch {
    return apiUrl;
  }
};

const getBaseUrl = () => {
  const localUrl = import.meta.env.VITE_LOCAL_API_URL;
  const testUrl = import.meta.env.VITE_TEST_API_URL;
  const prodUrl = import.meta.env.VITE_API_URL;
  // An explicit override, for running the dev server against a deployed backend.
  const explicitUrl = import.meta.env.VITE_API_BASE_URL;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (explicitUrl) return explicitUrl;

  // The dev server always talks to the local backend, whatever address the page was
  // opened on. Matching "localhost" in the whole URL — as this used to — meant that
  // opening the very same dev server at http://192.168.1.162:3000, to test from a
  // phone or to compare load times, silently switched the app to the production
  // backend: the UI looked fine and login failed, because local credentials were
  // being posted to production.
  if (import.meta.env.DEV) {
    const url = onPageHost(localUrl || 'http://localhost:6019', hostname);
    console.log('Using local API URL:', url);
    return url;
  }

  // A built app served from this machine — `vite preview`, or a local static server.
  if (isLocalHost(hostname)) {
    const url = onPageHost(localUrl || 'http://localhost:6019', hostname);
    console.log('Using local API URL:', url);
    return url;
  }

  // Matched on the hostname rather than the whole URL, which also took a path or a
  // query string containing "test" as the test environment.
  if (hostname.includes('test')) {
    const url = testUrl || 'https://backend.app.patentloop.com';
    console.log('Using test API URL:', url);
    return url;
  }

  const url = prodUrl || testUrl || 'https://backend.app.patentloop.com';
  console.log('Using production API URL:', url);
  return url;
};

export const API_CONFIG = {
  get baseUrl() {
    return getBaseUrl();
  },
  endpoints: {
    auth: {
      login: '/user/login',
      signup: '/user/signup',
      confirmSignup: '/user/confirm_signup',
      resendCode: '/user/resend_code',
      forgotPassword: '/user/forgot_password',
      confirmNewPassword: '/user/confirm_new_password',
      logout: '/user/logout',
      checkLogin: '/user/check_login',
      oauthExchange: '/user/oauth/exchange',
    },
    user: {
      fetchSettings: '/user/fetch_settings',
      updateSettings: '/user/update_settings',
      savePrompt: '/user/save_prompt',
      updatePrompt: '/user/update_prompt',
      fetchPrompts: '/user/fetch_prompts',
      deletePrompt: '/user/delete_prompt',
      saveEmailTemplate: '/user/save_email_template',
      fetchEmailTemplates: '/user/fetch_email_templates',
      deleteEmailTemplate: '/user/delete_email_template',
      autoGenerateEmailTemplate: '/user/auto_generate_email_template',
      saveBrowserState: '/user/save_browser_state',
      getBrowserState: '/user/get_browser_state',
      submitSupportRequest: '/user/submit_support_request',
      sendAnalysisEmail: '/user/send_analysis_email',
    },
    chat: {
      sendMessage: '/chat/message',
      getMessage: '/chat/get_message',
      feedback: '/chat/feedback',
      uploadImage: '/chat/upload_image',
      imageDecision: '/chat/image_decision',
    },
    session: {
      addSession: '/session/add_session',
      saveSession: '/session/save_session',
      autoSaveUnsavedSession: '/session/auto_save_unsaved_session',
      getUnsavedSession: '/session/get_unsaved_session',
      markSessionAsUserSaved: '/session/mark_session_as_user_saved',
    },
  },
  timeout: 30000,
  withCredentials: true,
};

export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;
